import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { countries as countriesList } from "countries-list";
import { jwtDecode } from "jwt-decode";
import BottomNav from "./BottomNav";
import TrippinPostView from "./TrippinPostView";
import axiosInstance from "../api/axiosInstance";
import { requestNotificationPermission, initializePushNotifications } from "../firebase";
import { CircularProgress } from '@mui/material';
import "../styles/styles.css";

export default function Home() {
  const [userId, setUserId] = useState(null);
  const [feed, setFeed] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;

  const [selectedView, setSelectedView] = useState("For You");

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const getCountryName = (isoCode) => {
    return countriesList[isoCode]?.name || isoCode || "Unknown Country";
  };

  const handleViewChange = async (event) => {
    const value = event.target.value;
    setSelectedView(value);

    setPage(1);
    setFeed([]);
    setHasMore(true);

    if (value === "Country") {
      await fetchCountries();
      setSelectedCountry("");
      setSelectedCity("");
      setAvailableCities([]);
      setIsFilterDialogOpen(true);
    }
    else {
      fetchFeed(value, 1, null, null);
    }
  };

  const fetchFeed = async (
    type = "For You",
    pageNum = 1,
    country = null,
    city = null
  ) => {
    try {
      setLoading(true);
      setError(null);

      let feedTypeParam = "";
      if (type === "For You") feedTypeParam = "forYou";
      else if (type === "Following") feedTypeParam = "following";
      else if (type === "Country") feedTypeParam = "country";

      let url = `/feed?type=${feedTypeParam}&page=${pageNum}&limit=${limit}`;

      if (country) {
        url += `&country=${country}`;
      }
      if (city) {
        url += `&city=${encodeURIComponent(city)}`;
      }

      const resp = await axiosInstance.get(url);
      const data = resp.data;

      if (pageNum === 1) {
        setFeed(data.feed);
      }
      else {
        setFeed((prev) => [...prev, ...data.feed]);
      }

      setHasMore(data.hasMore);
    } catch (err) {
      console.error("Error fetching feed:", err);
      setError("Failed to fetch feed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (!hasMore) return;
    setPage((prev) => prev + 1);
  };

  const fetchCountries = async () => {
    try {
      const response = await axiosInstance.get("/feed/countries");
      setAvailableCountries(response.data.countries);
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };

  const fetchCities = async (countryISO) => {
    if (!countryISO) return;
    try {
      const response = await axiosInstance.get(`/feed/cities?country=${countryISO}`);
      setAvailableCities(response.data.cities);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const handleCountryChange = async (e) => {
    const chosenCountry = e.target.value;
    setSelectedCountry(chosenCountry);
    setSelectedCity("");
    setAvailableCities([]);

    if (chosenCountry) {
      await fetchCities(chosenCountry);
    }
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  const handleApplyCountryFilter = () => {
    setIsFilterDialogOpen(false);
    setPage(1);
    setFeed([]);
    setHasMore(true);

    fetchFeed("Country", 1, selectedCountry, selectedCity);
  };

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found.");
      navigate("/login");
      return;
    }

    try {
      const decodedToken = jwtDecode(accessToken);
      const userIdFromToken = decodedToken.user_id;
      setUserId(userIdFromToken);
    } catch (err) {
      console.error("Error decoding access token:", err);
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const setupNotifications = async () => {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        return;
      }
      try {
        const decodedToken = jwtDecode(accessToken);
        if (!decodedToken.user_id) {
          return;
        }
        
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          console.log("Web notifications permission granted.");
          await requestNotificationPermission();
        }
        else {
          console.log("Web notifications permission denied.");
        }
        await initializePushNotifications();
      } catch (error) {
        console.error("Error setting up notifications:", error);
      }
    };
    setupNotifications();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchFeed("For You", 1, null, null);
    }
  }, [userId]);

  useEffect(() => {
    if (page > 1) {
      if (selectedView === "For You" || selectedView === "Following") {
        fetchFeed(selectedView, page, null, null);
      }
      else if (selectedView === "Country" && selectedCountry) {
        fetchFeed("Country", page, selectedCountry, selectedCity);
      }
    }
  }, [page]);

  if (loading && feed.length === 0) {
    return (
      <>
        <div className="loading-container">
          <CircularProgress size={40} sx={{ color: 'black' }} />
        </div>
        <BottomNav />
      </>
    );
  }
  
  if (error) return <div>{error}</div>;

  return (
    <div className="home-container">
      <div className="top-nav">
        <div className="dropdown-container">
          <Select
            value={selectedView}
            onChange={handleViewChange}
            variant="standard"
            disableUnderline
            displayEmpty
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              color: "#333",
              width: "100px",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                padding: "0 4px",
              },
              "& .MuiSelect-icon": {
                marginLeft: "4px",
              },
            }}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="For You">For You</MenuItem>
            <MenuItem value="Following">Following</MenuItem>
            <MenuItem value="Country">Country</MenuItem>
          </Select>
        </div>

        <div className="icons-container">
          <SearchIcon
            className="icon"
            onClick={() => navigate("/search")}
          />
          <NotificationsIcon
            className="icon"
            onClick={() => navigate("/notifications")}
          />
        </div>
      </div>

      {/* FEED DISPLAY */}
      {feed.length > 0 ? (
        <TrippinPostView posts={feed} enableRouting={false} />
      ) : (
        <div className="loading-container" style={{ height: "unset" }}>
          <p style={{ fontSize: "18px", color: "#555", textAlign: "center", marginTop: "50px" }}>
            No posts available
          </p>
        </div>
      )}

      {/* Example load-more button, or you could do infinite scroll */}
      {hasMore && !loading && (
        <Button onClick={handleLoadMore} variant="contained" sx={{ mt: 2 }}>
          Load More
        </Button>
      )}
      {loading && feed.length > 0 && <p>Loading more...</p>}

      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              padding: "14px",
              maxWidth: "400px",
              width: "100%",
            },
          },
        }}
      >
        <DialogTitle>Filter by Country/City</DialogTitle>
        <DialogContent>
          {/* COUNTRY SELECT */}
          <Select
            fullWidth
            value={selectedCountry}
            onChange={handleCountryChange}
            displayEmpty
            variant="outlined"
            sx={{
              marginBottom: "1rem",
              backgroundColor: "#f9f9f9",
              border: "none",
              padding: "4px 0",
              "& .MuiSelect-select": {
                padding: "4px 8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            <MenuItem value="">Select Country</MenuItem>
            {availableCountries.map((countryIso) => (
              <MenuItem key={countryIso} value={countryIso}>
                {getCountryName(countryIso.toUpperCase())}
              </MenuItem>
            ))}
          </Select>

          {/* CITY SELECT (optional) */}
          {availableCities.length > 0 && (
            <Select
              fullWidth
              value={selectedCity}
              onChange={handleCityChange}
              displayEmpty
              variant="outlined"
              sx={{
                backgroundColor: "#f9f9f9",
                border: "none",
                padding: "4px 0",
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="">All Cities</MenuItem>
              {availableCities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsFilterDialogOpen(false)}
            sx={{ textTransform: "none", color: "#2c3e50" }}
          >
            Close
          </Button>
          <Button
            onClick={handleApplyCountryFilter}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </div>
  );
}
